// utils/telefonosRepetidos.ts — ¿hay usuarios del equipo que comparten teléfono? (round 2026-10-30)
//
// El asistente de WhatsApp identifica a quien escribe por su número, así que dos personas del equipo (superadministrador,
// administrador, aseador) con el mismo teléfono lo dejan sin saber quién es quién. El servidor ya impide crear un duplicado
// nuevo, pero los que ya existían siguen ahí y nadie los veía. Estas funciones puras detectan los repetidos en la lista que la
// pantalla YA cargó (no piden nada más) para avisarlos.
//
// CRITERIO DE COMPARACIÓN: por dígitos del número completo, con código de país. Un dato antiguo guardado sin código se compara
// como lo que inequívocamente es (un móvil chileno: 9 dígitos que empiezan con 9, «56» + 9 dígitos, o «09» + 8 dígitos), así
// «912345678» y «+56912345678» cuentan como el MISMO número. Es solo un AVISO: no bloquea ni cambia nada.
import { isAdminRole } from "./roles.utils";
import { interpretarTelefonoGuardado } from "./telefonoE164";

export interface UsuarioConTelefono {
  id: number | string;
  nombre: string;
  telefono?: string | null;
  /** Rol de la persona (decide la frase del aviso: el asistente de WhatsApp solo atiende a administración). */
  rol?: string | string[] | null;
}

export interface GrupoTelefonoRepetido {
  /** Dígitos con los que se comparó (código de país incluido). */
  clave: string;
  usuarios: { id: number | string; nombre: string; rol?: string | string[] | null }[];
}

/** Dígitos con los que se compara un teléfono, o `null` si no hay nada que comparar (vacío o demasiado corto). */
export function claveDeComparacionTelefono(valor: unknown): string | null {
  const t = interpretarTelefonoGuardado(valor);
  switch (t.estado) {
    case "vacio":
      return null;
    case "completo":
      return t.canonico.slice(1);
    case "sin_codigo": {
      if (t.sugerencia) return t.sugerencia.slice(1);
      if (/^09[0-9]{8}$/.test(t.digitos)) return `56${t.digitos.slice(1)}`;
      return t.digitos.length >= 8 ? t.digitos : null;
    }
    default: {
      const digitos = t.texto.replace(/[^0-9]/g, "");
      return digitos.length >= 8 ? digitos : null;
    }
  }
}

/** Los grupos de usuarios que comparten teléfono (de dos en adelante), en el orden en que aparecen en la lista. */
export function encontrarTelefonosRepetidos(usuarios: readonly UsuarioConTelefono[]): GrupoTelefonoRepetido[] {
  const porClave = new Map<string, GrupoTelefonoRepetido>();
  for (const u of usuarios) {
    const clave = claveDeComparacionTelefono(u.telefono);
    if (clave === null) continue;
    const grupo = porClave.get(clave) ?? { clave, usuarios: [] };
    grupo.usuarios.push({ id: u.id, nombre: String(u.nombre ?? "").trim() || `Usuario ${u.id}`, ...(u.rol !== undefined ? { rol: u.rol } : {}) });
    porClave.set(clave, grupo);
  }
  return [...porClave.values()].filter((g) => g.usuarios.length >= 2);
}

/** «A y B», «A, B y C». */
export function unirNombres(nombres: readonly string[]): string {
  if (nombres.length <= 1) return nombres.join("");
  return `${nombres.slice(0, -1).join(", ")} y ${nombres[nombres.length - 1]}`;
}

/**
 * La frase que ve el administrador por cada grupo. El MOTIVO depende de quiénes son: el asistente de WhatsApp solo conversa con
 * administradores y superadministradores (decisión del dueño), así que solo a ellos se les dice que el bot no podrá responderles;
 * a dos aseadoras con el mismo número el bot nunca les responde de todos modos, y el motivo es que cada persona del equipo
 * debe tener su propio número. Si algún nombrado es de administración, rige el motivo del bot; sin rol conocido, el general.
 */
export function fraseTelefonoRepetido(grupo: GrupoTelefonoRepetido): string {
  const inicio = `Estos usuarios comparten teléfono: ${unirNombres(grupo.usuarios.map((u) => u.nombre))}. `;
  const usanElBot = grupo.usuarios.some((u) => u.rol !== undefined && u.rol !== null && isAdminRole(u.rol));
  return (
    inicio +
    (usanElBot
      ? "El bot de WhatsApp no podrá responderles hasta que cada uno tenga un número distinto."
      : "Cada persona del equipo debe tener su propio número.")
  );
}
